import { useCallback, useEffect, useRef, useState } from "react";
import SpeechRecognition, {
  useSpeechRecognition,
} from "react-speech-recognition";

interface UseSpeechToTextReturn {
  transcript: string;
  listening: boolean;
  startListening: () => Promise<void>;
  stopListening: () => void;
  toggleListening: () => void;
  resetTranscript: () => void;
  browserSupportsSpeechRecognition: boolean;
}

interface UseSpeechToTextOptions {
  onStopListening?: (text: string) => void;
  onUpdateTranscript?: (text: string) => void; // 실시간 업데이트 콜백
  timeout?: number; // 기본값: 3500ms
  language?: string; // 기본값: 'ko-KR'
}

/**
 * Web Speech API를 활용한 음성-텍스트 변환 훅
 *
 * @param options - 음성 인식 옵션
 * @param options.onStopListening - 음성 인식 종료 시 콜백 (인식된 텍스트 전달)
 * @param options.onUpdateTranscript - 실시간 transcript 업데이트 콜백
 * @param options.timeout - 침묵 후 자동 종료 시간 (ms, 기본값: 3500)
 * @param options.language - 인식 언어 (기본값: 'ko-KR')
 *
 * @returns 음성 인식 상태 및 제어 함수들
 *
 * @example
 * ```tsx
 * const { listening, startListening, transcript } = useSpeechToText({
 *   onStopListening: (text) => setPrompt(text),
 *   onUpdateTranscript: (text) => setDisplayText(text),
 *   timeout: 3500,
 *   language: 'ko-KR'
 * });
 * ```
 */
const useSpeechToText = (
  options: UseSpeechToTextOptions = {}
): UseSpeechToTextReturn => {
  const {
    onStopListening,
    onUpdateTranscript,
    timeout = 3500,
    language = "ko-KR",
  } = options;

  // 네이티브 모드 감지
  const isNativeMode =
    typeof window !== "undefined" && !!window?.Android?.sttStart;

  const [transcript, setTranscript] = useState<string>("");
  const [nativeTranscript, setNativeTranscript] = useState<string>("");
  const [nativeListening, setNativeListening] = useState<boolean>(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const {
    listening: browserListening,
    transcript: browserTranscript,
    resetTranscript: browserResetTranscript,
    browserSupportsSpeechRecognition,
  } = useSpeechRecognition();

  // 현재 모드에 맞는 상태
  const listening = isNativeMode ? nativeListening : browserListening;
  const currentTranscript = isNativeMode ? nativeTranscript : browserTranscript;

  // 네이티브 함수들
  const startNativeListening = useCallback(() => {
    window.Android?.sttStart();
    setNativeListening(true);
  }, []);

  const stopNativeListening = useCallback(() => {
    window.Android?.sttStop();
    setNativeListening(false);
  }, []);

  const resetNativeTranscript = useCallback(() => {
    setNativeTranscript("");
  }, []);

  // 음성 인식 시작
  const startListening = useCallback(async () => {
    if (isNativeMode) {
      resetNativeTranscript();
      startNativeListening();
    } else {
      if (!browserSupportsSpeechRecognition) return;
      setTranscript("");
      browserResetTranscript();
      await SpeechRecognition.startListening({
        language,
        continuous: true,
      });
    }
  }, [
    isNativeMode,
    browserSupportsSpeechRecognition,
    browserResetTranscript,
    language,
    resetNativeTranscript,
    startNativeListening,
  ]);

  // 음성 인식 종료
  const stopListening = useCallback(() => {
    if (isNativeMode) {
      stopNativeListening();
    } else {
      if (!browserSupportsSpeechRecognition) return;
      SpeechRecognition.stopListening();
    }
  }, [isNativeMode, browserSupportsSpeechRecognition, stopNativeListening]);

  // 토글
  const toggleListening = useCallback(() => {
    if (listening) stopListening();
    else startListening();
  }, [listening, stopListening, startListening]);

  // resetTranscript 함수
  const resetTranscript = useCallback(() => {
    if (isNativeMode) {
      resetNativeTranscript();
    } else {
      browserResetTranscript();
    }
    setTranscript("");
  }, [isNativeMode, browserResetTranscript, resetNativeTranscript]);

  // 네이티브 결과 수신 핸들러
  useEffect(() => {
    if (isNativeMode) {
      window.onSttResult = (result) => {
        const { partial, full } = result ?? {};
        if (partial) setNativeTranscript(partial);
        if (full) setNativeTranscript(full);
      };

      return () => {
        window.onSttResult = undefined;
      };
    }
  }, [isNativeMode]);

  // 인식된 텍스트 업데이트 및 onUpdateTranscript 콜백 호출
  useEffect(() => {
    setTranscript(currentTranscript);
    // listening 상태일 때만 onUpdateTranscript 호출 (키보드 입력 덮어쓰기 방지)
    if (listening && onUpdateTranscript && typeof onUpdateTranscript === "function") {
      onUpdateTranscript(currentTranscript);
    }
  }, [currentTranscript, onUpdateTranscript, listening]);

  // 침묵 후 자동 종료 및 콜백
  useEffect(() => {
    if (listening) {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      timeoutRef.current = setTimeout(() => {
        stopListening();
        timeoutRef.current = null;

        if (onStopListening && typeof onStopListening === "function") {
          onStopListening(transcript);
        }
      }, timeout);
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [listening, transcript, stopListening, onStopListening, timeout]);

  return {
    transcript,
    listening,
    startListening,
    stopListening,
    toggleListening,
    resetTranscript,
    browserSupportsSpeechRecognition,
  };
};

export default useSpeechToText;
